import { EWorksheetStatus } from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  BaseWorksheetFormConfig,
  BaseWorksheetFormValues,
  buildBaseWorksheetFormInitialValues,
  StoredTermAndPlan,
  useBaseWorksheetFormFields,
  useBaseWorksheetFormReady,
} from '@src/components/WorksheetForm'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { EasyPayWorksheet, EditEasyPayWorksheetDto } from '@src/data/types/EasyPayWorksheetSchema'
import { useMemo } from 'react'

export type EasyPayEditValues = BaseWorksheetFormValues

export type EasyPayEditSchema = ReturnType<typeof useEasyPayEditSchema>

export type EasyPayEditSubmitValues = yup.InferSubmit<EasyPayEditSchema>

export const useEasyPayEditSchema = (config: BaseWorksheetFormConfig) => {
  const fields = useBaseWorksheetFormFields(config)

  return useMemo(
    () =>
      yup.object({
        amountRequested: fields.amountRequested,
        paymentPlanId: fields.paymentPlanId,
        term: fields.term,
        paymentFrequency: fields.paymentFrequency,
        activationDate: fields.activationDate,
        firstPaymentDate: fields.firstPaymentDate,
        includeProtection: fields.includeProtection,
        isHighEstimate: fields.isHighEstimate,
      }),
    [fields],
  )
}

export const fromWorksheet = (defaults: EasyPayEditValues, worksheet: EasyPayWorksheet): EasyPayEditValues =>
  buildBaseWorksheetFormInitialValues(defaults, worksheet as BaseWorksheetDto, null)

export const useEasyPayEditForm = (config: BaseWorksheetFormConfig, worksheet: EasyPayWorksheet) => {
  const schema = useEasyPayEditSchema(config)
  const isReady = useBaseWorksheetFormReady(config)

  const defaultValues = useMemo<EasyPayEditValues | undefined>(() => {
    if (!isReady) return undefined
    return fromWorksheet(schema.getDefault() as EasyPayEditValues, worksheet)
  }, [isReady, schema, worksheet])

  return { schema, defaultValues, isReady }
}

export const getStoredTermAndPlan = (worksheet: EasyPayWorksheet): StoredTermAndPlan | null =>
  worksheet.paymentPlanId && worksheet.term ? { paymentPlanId: worksheet.paymentPlanId, term: worksheet.term } : null

export const toEditEasyPayWorksheetDto = (
  values: EasyPayEditSubmitValues,
  worksheet: EasyPayWorksheet,
): EditEasyPayWorksheetDto => ({
  creditApplicationId: worksheet.creditApplicationId,
  versionTag: worksheet.versionTag,
  status: EWorksheetStatus.Active,
  amountRequested: values.amountRequested,
  term: values.term,
  paymentFrequency: values.paymentFrequency,
  deliveryOn: values.activationDate.toString(),
  firstPaymentOn: values.firstPaymentDate.toString(),
})
